import { useMemo } from 'react';

import path from 'path';

import { Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useSWR from 'swr';

import {
	ActionBarAction,
	ActionButton,
	ActionButtonStyle,
	Card,
	GeneralLoader,
	NotificationCard,
	useActionBarContribution,
} from '~components';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments } from '~constants';
import { StatusVariant } from '~enums';
import { cancellationMiddleware } from '~lib/swr';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';
import { triggerBlobDownload } from '~utils/fileUtils';

import WebhooksDataGrid from '../dataGrids/webhooksDataGrid';
import WebhooksService from '../services/webhooksService';

const WebhooksOverviewContainer = () => {
	const { palette } = useTheme();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const webhooksService = getService(WebhooksService);

	const { data, error, isLoading, isValidating } = useSWR(
		webhooksService.getRentalWebhookCacheKey(),
		async ([_path, _endpoint]: string[]) => await webhooksService.getRentalWebhook(),
		{
			keepPreviousData: true,
			revalidateOnMount: true,
			shouldRetryOnError: () => false,
		}
	);

	// This is a bit messed up at the moment due to UI/backend mismatch.
	// Ui wants us to show it in a data grid, while the backend only returns 1 webhook per endpoint.
	// currently that's only rental. In the future there will most likely be a list call.
	const certificates = useMemo(() => {
		return {
			results: data ? [data] : [],
			pageSize: 10,
			page: 1,
		};
	}, [data]);

	const { data: certificate } = useSWR(
		webhooksService.getSkopeiConnectPublicKeyCertificateCacheKey(),
		(_args: [string], options: { signal?: AbortSignal }) => {
			return webhooksService.getSkopeiConnectPublicKeyCertificate(options.signal);
		},
		{ use: [cancellationMiddleware] }
	);

	const actions = useMemo<ActionBarAction[]>(() => {
		return certificate?.file ?
				[
					{
						id: `download-certificate`,
						label: t('webhook.labels.downloadCertificate', { ns: 'entities' }),
						icon: <VectorImage name='icon/download' />,
						onClick: () => {
							if (certificate?.file) {
								triggerBlobDownload(certificate?.file, certificate?.fileName);
							}
						},
					},
				]
			:	[];
	}, [certificate]);

	useActionBarContribution(actions, { id: `webhook-certificate`, overflowLimit: 1 });

	return (
		<Stack gap={Dimens.spacing4xl}>
			{data && certificate && (
				<NotificationCard
					title={t('webhook.labels.certificateIsDueIn', {
						ns: 'entities',
						value: certificate?.expiryDate.fromNow() ?? '',
					})}
					statusVariant={StatusVariant.Warning}
				/>
			)}
			<Card>
				{isLoading ?
					<GeneralLoader />
				: data || (error as ApiError)?.axiosError?.status != 404 ?
					<WebhooksDataGrid
						webhooks={certificates?.results ?? []}
						isLoading={isLoading || isValidating}
						error={(error as ApiError)?.getDescription()}
					/>
				:	<Stack
						alignItems={'center'}
						justifyContent={'center'}
						gap={Dimens.spacing4xl}
						sx={{ paddingY: Dimens.spacing9xl }}
					>
						<VectorImage name='illustration/webhooks' />
						<Stack
							alignContent={'center'}
							alignItems={'center'}
							gap={Dimens.spacingMd}
							sx={{ maxWidth: '600px' }}
						>
							<Typography variant='headingLg' color={palette.textPrimary.main}>
								{t('webhook.labels.setupYourWebhook', { ns: 'entities' })}
							</Typography>
							<Typography variant='labelLg' color={palette.textQuaternary.main} align='center'>
								{t('webhook.labels.setupYourWebhookDescription', { ns: 'entities' })}
							</Typography>
						</Stack>
						<Stack direction={'row'} gap={Dimens.spacing2xl}>
							<ActionButton
								startIcon={<VectorImage name='icon/webhook' />}
								label={t('webhook.labels.create', { ns: 'entities' })}
								variant={StatusVariant.Brand}
								onClick={() =>
									navigate(path.join('/' + pagePathSegments.Webhooks, pagePathSegments.Create))
								}
							/>
							<ActionButton
								startIcon={<VectorImage name='icon/file' />}
								label={t('viewDocumentation', { ns: 'system' })}
								style={ActionButtonStyle.Bordered}
								variant={StatusVariant.Neutral}
								onClick={() => navigate('/wiki/fd9b6e761b87')}
							/>
						</Stack>
					</Stack>
				}
			</Card>
		</Stack>
	);
};

export default WebhooksOverviewContainer;
